import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { subnetsAPI, computersAPI, networkDevicesAPI, serversAPI, Subnet } from '../services/api';
import { Computer, NetworkDevice, Server } from '../types';

interface IpRow {
  ipAddress: string;
  name: string;
  type: string;
  location?: string;
  status?: string;
  department?: string;
  user?: string;
  macAddress?: string;
}

const ipToInt = (ip: string): number => {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some((p) => Number.isNaN(p))) return 0;
  return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
};

const isIPInSubnet = (ip: string, cidr: string): boolean => {
  const [networkIP, prefixLength] = cidr.split('/');
  const prefix = parseInt(prefixLength ?? '0', 10);
  if (Number.isNaN(prefix) || prefix < 0 || prefix > 32) return false;
  const mask = (0xffffffff << (32 - prefix)) >>> 0;
  const ipInt = ipToInt(ip);
  const networkInt = ipToInt(networkIP);
  return (ipInt & mask) === (networkInt & mask);
};

const ITIPOverview: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [devices, setDevices] = useState<{
    computers: Computer[];
    servers: Server[];
    networkDevices: NetworkDevice[];
  }>({ computers: [], servers: [], networkDevices: [] });

  const [selectedSubnetId, setSelectedSubnetId] = useState<string | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [subnetData, computerData, serverData, networkDeviceData] = await Promise.all([
        subnetsAPI.getAll(),
        computersAPI.getAll(),
        serversAPI.getAll(),
        networkDevicesAPI.getAll()
      ]);
      setSubnets(subnetData);
      setDevices({
        computers: computerData,
        servers: serverData,
        networkDevices: networkDeviceData
      });
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju IP pregleda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const selectedSubnet = useMemo(
    () =>
      selectedSubnetId === 'all'
        ? null
        : subnets.find((s) => s._id === selectedSubnetId) || null,
    [selectedSubnetId, subnets]
  );

  const rows: IpRow[] = useMemo(() => {
    if (!selectedSubnet && selectedSubnetId !== 'all') return [];

    const cidr = selectedSubnet ? selectedSubnet.cidr : null;

    const list: IpRow[] = [];

    // Network devices
    devices.networkDevices.forEach((d) => {
      if (!d.ipAddress) return;
      if (cidr && !isIPInSubnet(d.ipAddress, cidr)) return;
      list.push({
        ipAddress: d.ipAddress,
        name: d.name,
        type: d.type,
        location: d.location,
        status: d.status,
        department: '',
        user: '',
        macAddress: d.macAddress
      });
    });

    // Servers
    devices.servers.forEach((s) => {
      if (!s.ipAddress) return;
      if (cidr && !isIPInSubnet(s.ipAddress, cidr)) return;
      list.push({
        ipAddress: s.ipAddress,
        name: s.name,
        type: 'server',
        location: s.location,
        status: s.status,
        department: '',
        user: '',
        macAddress: s.macAddress
      });
    });

    // Computers
    devices.computers.forEach((c) => {
      if (!c.ipAddress) return;
      if (cidr && !isIPInSubnet(c.ipAddress, cidr)) return;
      list.push({
        ipAddress: c.ipAddress,
        name: c.name,
        type: 'computer',
        location: c.location,
        status: c.status,
        department: c.department,
        user: c.user,
        macAddress: c.macAddress
      });
    });

    return list.sort((a, b) => ipToInt(a.ipAddress) - ipToInt(b.ipAddress));
  }, [devices, selectedSubnet, selectedSubnetId]);

  const filteredRows = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.ipAddress.toLowerCase().includes(s) ||
      r.name.toLowerCase().includes(s) ||
      (r.location || '').toLowerCase().includes(s) ||
      (r.user || '').toLowerCase().includes(s) ||
      (r.department || '').toLowerCase().includes(s)
    );
  });

  const percentUsed = selectedSubnet
    ? selectedSubnet.totalIPs > 0
      ? Math.round((selectedSubnet.usedIPs / selectedSubnet.totalIPs) * 100)
      : 0
    : 0;

  return (
    <div className="p-4">
      <Toast ref={toast} />

      {error && <Message severity="error" text={error} className="mb-3" />}

      <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="m-0">IP Adrese - Pregled</h2>
        <div className="flex align-items-center gap-2">
          <Dropdown
            value={selectedSubnetId}
            options={[
              { label: 'Svi subneti', value: 'all' },
              ...subnets.map((s) => ({ label: s.name, value: s._id }))
            ]}
            onChange={(e) => setSelectedSubnetId(e.value)}
            placeholder="Filtriraj po subnet-u"
            style={{ minWidth: '220px' }}
            appendTo="self"
          />
          <Button
            icon="pi pi-refresh"
            outlined
            onClick={() => void load()}
            aria-label="Osveži"
          />
        </div>
      </div>

      {selectedSubnet && (
        <div className="mb-4">
          <Card>
            <div className="flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h3 className="m-0">{selectedSubnet.name}</h3>
                <div className="text-600">
                  {selectedSubnet.cidr} &nbsp;|&nbsp; Ukupno: {selectedSubnet.totalIPs}
                </div>
              </div>
              <div className="flex gap-4">
                <div>
                  <div className="text-600">Korišćeno</div>
                  <div className="font-bold">{selectedSubnet.usedIPs}</div>
                </div>
                <div>
                  <div className="text-600">Slobodno</div>
                  <div className="font-bold">{selectedSubnet.freeIPs}</div>
                </div>
                <div>
                  <div className="text-600">% iskorišćenosti</div>
                  <div className="font-bold">{percentUsed}%</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h3 className="m-0">Uređaji sa IP adresama</h3>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraga po IP, nazivu, lokaciji..."
            style={{ width: 'min(320px, 100%)' }}
          />
        </div>

        <DataTable
          value={filteredRows}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema uređaja sa IP adresama"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="ipAddress" header="IP Adresa" sortable />
          <Column field="name" header="Naziv" sortable />
          <Column field="type" header="Tip" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column
            field="status"
            header="Status"
            body={(row: IpRow) =>
              row.status ? <Tag value={row.status} /> : <span>-</span>
            }
          />
          <Column field="department" header="Departman" sortable />
          <Column field="user" header="Korisnik" sortable />
          <Column field="macAddress" header="MAC Adresa" sortable />
        </DataTable>
      </Card>
    </div>
  );
};

export default ITIPOverview;






